import * as React from 'react'
// libs
import { useDispatch } from 'react-redux'
import { useMutation, useQuery } from '@apollo/client/react'
// components
import { LoaderWithOverlay } from 'components/UI/Loader'
// context
import { TableManagementContext } from 'containers/Search/layers/BL'
// graphql
import { GROUP_SECURITIES_QUERY } from 'graphql/groupSecurity/queries'
import {
  GROUP_SECURITY_CREATE_MUTATION,
  GROUP_SECURITY_DELETE_MUTATION,
  GROUP_SECURITY_UPDATE_MUTATION
} from 'graphql/groupSecurity/mutations'
// hooks
import { useCurrentUser } from 'hooks/index'
// redux
import { setCurrentUser } from 'store/reducers/currentUser'
// types
import { TRefetch } from 'types/refetch'
import {
  ISecurityGroup,
  ISecurityGroupResponse,
  ISecurityGroupsResponse,
  ISecurityGroupsQueryArgs
} from 'types/graphql'
// utils
import get from 'lodash/get'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

type Props = React.PropsWithChildren<unknown>

type SecurityGroupCreateResponse = {
  securityGroupCreate: ISecurityGroupResponse
}

type SecurityGroupUpdateResponse = {
  securityGroupUpdate: ISecurityGroupResponse
}

interface IContext {
  data: ISecurityGroup[]
  refetch: TRefetch<ISecurityGroupsResponse, ISecurityGroupsQueryArgs>
  companyId: string
  isLoading: boolean
  totalCount: number
  isRefetching: boolean
  deleteGroupSecurity: (id: string, hideModal: () => void) => Promise<void>
  createGroupSecurity: (
    input: Record<string, any>,
    users: Record<string, any>[],
    hideModal: () => void
  ) => Promise<void>
  updateGroupSecurity: (
    id: string,
    input: Record<string, any>,
    users: Record<string, any>[],
    hideModal: () => void
  ) => Promise<void>
}

export const GroupSecurityContext = React.createContext<IContext>({} as any)

const GroupSecurityAPI: React.FC<Props> = ({ children }) => {
  const dispatch = useDispatch()

  const currentUser = useCurrentUser()

  const { filter, page, perPage, search } = React.useContext(TableManagementContext)

  const [createGroupSecurityMutation] = useMutation<SecurityGroupCreateResponse>(GROUP_SECURITY_CREATE_MUTATION)
  const [deleteGroupSecurityMutation] = useMutation(GROUP_SECURITY_DELETE_MUTATION)
  const [updateGroupSecurityMutation] = useMutation<SecurityGroupUpdateResponse>(GROUP_SECURITY_UPDATE_MUTATION)

  const { data, networkStatus, refetch } = useQuery(GROUP_SECURITIES_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        ...filter,
        name: search
      },
      pagination: {
        pageSkip: page * perPage,
        pageTake: perPage
      }
    }
  })

  const companyId = filter.companyId || currentUser.companyId
  const isLoading = networkStatus === 1
  const totalCount: number = get(data, 'securityGroups.pagination.totalCount')
  const groupSecurities: ISecurityGroup[] = get(data, 'securityGroups.data', [])

  // methods
  const shouldUpdateUser = (data) =>
    data.some((item) => item.user.value === currentUser.id || item.user.value === 'ALL')

  const createGroupSecurity = async (input, users, hideModal: () => void) => {
    try {
      const {
        data: {
          securityGroupCreate: { data: createdSecurityGroup }
        }
      } = await createGroupSecurityMutation({
        variables: {
          companyId,
          input
        }
      })

      if (shouldUpdateUser(users)) {
        dispatch(
          setCurrentUser({
            ...currentUser,
            securityGroups: [...currentUser.securityGroups, createdSecurityGroup]
          })
        )
      }

      await refetch()

      hideModal()
    } catch (error) {
      console.log('[GroupSecurityAPI.createGroupSecurity]: ', parseGraphQLErrorMessages(error))
    }
  }

  const deleteGroupSecurity = async (id: string, hideModal: () => void) => {
    try {
      await deleteGroupSecurityMutation({
        variables: {
          id,
          companyId
        }
      })

      if (currentUser.securityGroups.some((securityGroup) => securityGroup.id === id)) {
        dispatch(
          setCurrentUser({
            ...currentUser,
            securityGroups: currentUser.securityGroups.filter((securityGroup) => securityGroup.id !== id)
          })
        )
      }

      await refetch()

      hideModal()
    } catch (error) {
      console.log('[GroupSecurityAPI.deleteGroupSecurity]: ', parseGraphQLErrorMessages(error))
    }
  }

  const updateGroupSecurity = async (id: string, input, users, hideModal: () => void) => {
    try {
      const {
        data: {
          securityGroupUpdate: { data: updatedSecurityGroup }
        }
      } = await updateGroupSecurityMutation({
        variables: {
          id,
          companyId,
          input
        }
      })

      if (shouldUpdateUser(users)) {
        dispatch(
          setCurrentUser({
            ...currentUser,
            securityGroups: currentUser.securityGroups.map((securityGroup) => {
              if (securityGroup.id === updatedSecurityGroup.id) {
                return updatedSecurityGroup
              }
              return securityGroup
            })
          })
        )
      }

      await refetch()

      hideModal()
    } catch (error) {
      console.log('[GroupSecurityAPI.updateGroupSecurity]: ', parseGraphQLErrorMessages(error))
    }
  }

  const value = {
    refetch,
    companyId,
    totalCount,
    updateGroupSecurity,
    createGroupSecurity,
    deleteGroupSecurity,
    data: groupSecurities,
    isLoading: networkStatus === 1,
    isRefetching: networkStatus === 2
  }

  return (
    <>
      <LoaderWithOverlay isLoading={isLoading} height={'calc(100% - 49px)'} />
      {!isLoading && <GroupSecurityContext.Provider value={value}>{children}</GroupSecurityContext.Provider>}
    </>
  )
}

export default GroupSecurityAPI
